/**
 * Getting the app onto the Home Screen.
 *
 * This matters more here than for most web apps. iOS only delivers web push to a PWA that has
 * been added to the Home Screen, so installing is not a nicety to mention once and forget —
 * it is a step in onboarding, and on iOS it is a step the browser will not offer on our
 * behalf. Hence two paths: Chrome hands us an event to fire at a moment of our choosing, and
 * on iOS we have to explain the gesture ourselves.
 */

export type InstallState =
  /** Already on the Home Screen. Nothing to ask for. */
  | 'installed'
  /** The browser gave us a prompt to fire. */
  | 'promptable'
  /** No prompt is coming; the user has to do it by hand, and needs telling how. */
  | 'manual'
  /** Not installable here — a desktop browser that does not do this. */
  | 'unavailable';

export interface Environment {
  standalone: boolean;
  ios: boolean;
  promptHeld: boolean;
}

export function installState(environment: Environment): InstallState {
  if (environment.standalone) {
    return 'installed';
  }
  if (environment.promptHeld) {
    return 'promptable';
  }

  return environment.ios ? 'manual' : 'unavailable';
}

/** The Chrome-only event, which is not in the DOM types. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

let held: InstallPromptEvent | null = null;

/**
 * Holds on to the browser's install prompt instead of letting it fire itself.
 *
 * Returns a function that stops watching. `onChange` runs whenever the answer to
 * [`installState`] may have changed.
 */
export function watchForInstallPrompt(onChange: () => void): () => void {
  const capture = (event: Event): void => {
    // Suppressing the browser's own mini-infobar is the point: it appears at whatever moment
    // suits the browser, which is rarely a moment that suits somebody holding groceries.
    event.preventDefault();
    held = event as InstallPromptEvent;
    onChange();
  };

  const installed = (): void => {
    held = null;
    onChange();
  };

  window.addEventListener('beforeinstallprompt', capture);
  window.addEventListener('appinstalled', installed);

  return () => {
    window.removeEventListener('beforeinstallprompt', capture);
    window.removeEventListener('appinstalled', installed);
  };
}

export function promptHeld(): boolean {
  return held !== null;
}

/** Shows the held prompt. A prompt can only be shown once, so it is dropped afterwards. */
export async function offerInstall(): Promise<void> {
  const prompt = held;
  held = null;

  await prompt?.prompt();
}

export function detectEnvironment(): Environment {
  return {
    standalone: isStandalone(),
    ios: isIos(),
    promptHeld: promptHeld(),
  };
}

function isStandalone(): boolean {
  // Chrome answers the media query; iOS Safari sets a non-standard property instead.
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  const agent = navigator.userAgent;

  // An iPad on iPadOS 13+ claims to be a Mac, and the only reliable tell is that Macs do not
  // have a touchscreen.
  return (
    /iphone|ipad|ipod/i.test(agent) ||
    (agent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}
